/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useCallback, useMemo } from 'react';
import { View, Linking } from 'react-native';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import Link from './components/Link';
import DividerView from '@airasia-phoenix/react-native/components/DividerView';
import {
  getTextStyle,
  outlineStyleNone,
} from '@airasia-phoenix/react-native/utils/utils';
import { pd } from '@airasia-phoenix/react-native/tokens';
import styles from './styles';
import {
  WebFooterViewProps,
  DEFAULT_CHINA_LICENSE_PERMIT_INFO,
  ChinaLicenseItem,
} from './types';
import {
  DividerDirection,
  DividerType,
} from '@airasia-phoenix/react-native/components/DividerView/types';
import ConnectWithUs from './components/ConnectWithUs';
import DownloadApp from './components/DownloadApp';
import DisclaimerText from './components/DisclaimerText';
import { useSetting } from '@airasia-rn/account/hooks';
import { isChinaDomain } from '../../utils';

export const WebFooterView: React.FC<WebFooterViewProps> = ({
  primaryText,
  disclaimer,
  sections,
  actions,
  onLinkPress,
}) => {
  const { value: chinaLicensePermitInformation } = useSetting(
    'china_license_permit_information',
  );

  const finalDisclaimer = useMemo(() => {
    if (!disclaimer || !isChinaDomain()) {
      return disclaimer;
    }

    let licenseItems: ChinaLicenseItem[] = [];
    if (typeof chinaLicensePermitInformation === 'string') {
      try {
        licenseItems = JSON.parse(chinaLicensePermitInformation);
      } catch {
        licenseItems = [];
      }
    } else if (Array.isArray(chinaLicensePermitInformation)) {
      licenseItems = chinaLicensePermitInformation;
    }

    // Fallback to hardcoded default values as last resort if useSetting fails or is empty
    if (!Array.isArray(licenseItems) || licenseItems.length === 0) {
      licenseItems = DEFAULT_CHINA_LICENSE_PERMIT_INFO;
    }

    const updatedLinks = { ...disclaimer.links };
    const appendedPlaceholders: string[] = [];
    licenseItems.forEach((item, index) => {
      if (item?.text && item?.link) {
        const alreadyExists =
          Object.values(updatedLinks).some(
            (l) => l?.label === item.text || l?.url === item.link,
          ) || disclaimer.text.includes(item.text);

        if (!alreadyExists) {
          const key = `china_license_${index}`;
          updatedLinks[key] = {
            label: item.text,
            url: item.link,
          };
          appendedPlaceholders.push(`{${key}}`);
        }
      }
    });

    if (appendedPlaceholders.length === 0) {
      return disclaimer;
    }

    const separator = disclaimer.text
      ? disclaimer.text.endsWith(' ')
        ? ''
        : ' '
      : '';
    const updatedText = `${disclaimer.text}${separator}${appendedPlaceholders.join(' ')}`;

    return {
      ...disclaimer,
      text: updatedText,
      links: updatedLinks,
    };
  }, [disclaimer, chinaLicensePermitInformation]);

  const openUrl = useCallback((url: string) => {
    Linking.openURL(url);
  }, []);

  return (
    <View style={styles.footerContainer}>
      <View style={styles.footer}>
        {primaryText && (
          <TextView
            text={primaryText}
            style={getTextStyle(
              pd.sys.typescale.heading_3,
              pd.sys.colour.content_primary,
            )}
          />
        )}
        {(sections?.length > 0 || actions) && (
          <View style={styles.footerMainContent}>
            {sections?.length > 0 && (
              <View style={styles.sectionsContainer}>
                {sections.map((section, index) => (
                  <View style={styles.section} key={index}>
                    <View style={styles.sectionTitle}>
                      <TextView
                        text={section.title}
                        style={getTextStyle(
                          pd.sys.typescale.sub_body_text_medium,
                          pd.sys.colour.content_primary,
                        )}
                      />
                    </View>
                    {section.links.map((link, linkIndex) => (
                      <Link
                        key={linkIndex}
                        url={link.url}
                        style={{
                          maxWidth: 200,
                          ...outlineStyleNone(),
                        }}
                        onPress={() =>
                          onLinkPress?.(
                            section.label ?? section.title,
                            link.display_label ?? link.text,
                          )
                        }
                      >
                        <TextView
                          text={link.text}
                          style={[
                            getTextStyle(
                              pd.sys.typescale.caption_text,
                              pd.sys.colour.content_secondary,
                            ),
                            styles.link,
                          ]}
                        />
                      </Link>
                    ))}
                  </View>
                ))}
              </View>
            )}
            {actions && sections?.length > 0 && (
              <DividerView
                direction={DividerDirection.Horizontal}
                type={DividerType.Solid}
                thickness={1}
                width="100%"
                color={pd.sys.colour.outline_low.toString()}
                customViewStyle={[styles.divider, styles.actionDivider]}
              />
            )}
            {actions && (
              <View style={styles.actionContainer}>
                {actions.connect && (
                  <ConnectWithUs
                    data={actions.connect}
                    openUrl={openUrl}
                    onLinkPress={onLinkPress}
                  />
                )}
                {actions.downloads && (
                  <DownloadApp
                    data={actions.downloads}
                    openUrl={openUrl}
                    onLinkPress={onLinkPress}
                  />
                )}
              </View>
            )}
          </View>
        )}
        {finalDisclaimer &&
          (primaryText || sections?.length > 0 || actions) && (
            <DividerView
              direction={DividerDirection.Horizontal}
              type={DividerType.Solid}
              thickness={1}
              width="100%"
              color={pd.sys.colour.outline_low.toString()}
              customViewStyle={styles.divider}
            />
          )}
        {finalDisclaimer && (
          <DisclaimerText
            data={finalDisclaimer}
            openUrl={openUrl}
            onLinkPress={onLinkPress}
          />
        )}
      </View>
    </View>
  );
};

export default WebFooterView;
